import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Availability, BookingCharacter, PublicSessionPlan, SlotResponse } from '@gm-binder/shared';
import { api, ApiError } from '../api/client';
import { formatSlot } from '../lib/datetime';
import styles from './BookingPage.module.css';

const AVAIL_OPTIONS: { value: Availability; label: string; cls: string }[] = [
  { value: 'yes', label: '✓ Ci sono', cls: styles.yes },
  { value: 'maybe', label: '~ Forse', cls: styles.maybe },
  { value: 'no', label: '✕ No', cls: styles.no },
];

export function BookingPage() {
  const { token = '' } = useParams();
  const [plan, setPlan] = useState<PublicSessionPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [character, setCharacter] = useState<BookingCharacter | null>(null);
  const [avail, setAvail] = useState<Record<string, Availability>>({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setPlan(await api.getPublicPlan(token));
      } catch (e) {
        setError(e instanceof ApiError && e.status === 404 ? 'Link non valido o scaduto.' : 'Errore di caricamento.');
      }
    })();
  }, [token]);

  const sameChar = (r: { characterId?: string; characterName: string }, c: BookingCharacter) =>
    c.id && r.characterId
      ? r.characterId === c.id
      : r.characterName.toLowerCase() === c.name.toLowerCase();

  const selectCharacter = (c: BookingCharacter) => {
    setCharacter(c);
    setDone(false);
    const existing = plan?.responses.find((r) => sameChar(r, c));
    const map: Record<string, Availability> = {};
    existing?.slots.forEach((s) => {
      map[s.slotId] = s.availability;
    });
    setAvail(map);
    setComment(existing?.comment ?? '');
  };

  const submit = async () => {
    if (!character) return;
    const slots: SlotResponse[] = Object.entries(avail).map(([slotId, availability]) => ({
      slotId,
      availability,
    }));
    setSubmitting(true);
    try {
      const updated = await api.submitPlanResponse(token, {
        characterName: character.name,
        characterId: character.id,
        comment: comment.trim() || undefined,
        slots,
      });
      setPlan(updated);
      setDone(true);
    } catch {
      setError('Invio non riuscito. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmedSlot = useMemo(
    () => plan?.slots.find((s) => s.id === plan.confirmedSlotId) ?? null,
    [plan],
  );

  if (error && !plan) {
    return (
      <div className={styles.center}>
        <div>
          <div className={styles.brand}>🕯️ GM Binder</div>
          <p style={{ marginTop: 16 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className={styles.center}>
        <div className={styles.brand}>🕯️ Carico…</div>
      </div>
    );
  }

  const closed = plan.status === 'closed';

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.head}>
          <div className={styles.brand}>🕯️ GM Binder</div>
          <h1>{plan.title}</h1>
          <div className={styles.sub}>
            {plan.adventureTitle ? `${plan.adventureTitle} · ` : ''}
            {plan.instanceName}
            {plan.location ? ` · 📍 ${plan.location}` : ''}
          </div>
        </div>

        {confirmedSlot && (
          <div className={styles.confirmed}>
            <strong>✅ Sessione confermata:</strong> {formatSlot(confirmedSlot.start, confirmedSlot.durationMin)}
          </div>
        )}

        {plan.notes && (
          <div className={styles.card}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{plan.notes}</p>
          </div>
        )}

        {/* Step 1 — who are you? */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>1 · Chi sei?</h2>
          <div className={styles.sub}>Seleziona il tuo personaggio.</div>
          <div className={styles.charGrid}>
            {plan.characters.map((c) => {
              const responded = plan.responses.some((r) => sameChar(r, c));
              const active = !!character && (character.id ? character.id === c.id : character.name === c.name);
              return (
                <button
                  key={c.id ?? c.name}
                  type="button"
                  className={[styles.charBtn, active ? styles.charActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => selectCharacter(c)}
                >
                  <div className={styles.charName}>
                    {c.name} {responded ? '✓' : ''}
                  </div>
                  {c.personName && <div className={styles.charPerson}>{c.personName}</div>}
                </button>
              );
            })}
            {plan.characters.length === 0 && (
              <div className={styles.sub}>Nessun personaggio configurato per questa sessione.</div>
            )}
          </div>
        </div>

        {/* Step 2 — availability */}
        {character && !closed && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>2 · Quando puoi?</h2>
            <div className={styles.sub}>Indica la tua disponibilità per ogni proposta.</div>
            {plan.slots.map((slot) => (
              <div key={slot.id} className={styles.slot}>
                <div>
                  <div className={styles.slotWhen}>{formatSlot(slot.start, slot.durationMin)}</div>
                  {slot.note && <div className={styles.slotNote}>{slot.note}</div>}
                </div>
                <div className={styles.avail}>
                  {AVAIL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={[
                        styles.availBtn,
                        opt.cls,
                        avail[slot.id] === opt.value ? styles.on : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setAvail((a) => ({ ...a, [slot.id]: opt.value }))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {plan.slots.length === 0 && (
              <div className={styles.sub}>Il master non ha ancora proposto delle date.</div>
            )}

            <label className={styles.slotNote} htmlFor="comment">
              Un messaggio per il master (facoltativo)
            </label>
            <textarea
              id="comment"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={{
                background: 'var(--color-bg-inset)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-line)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-2) var(--space-3)',
                font: 'inherit',
                resize: 'vertical',
              }}
            />

            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting || plan.slots.length === 0}
              style={{
                background: 'var(--color-gold)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3)',
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {done ? '✓ Risposta salvata — aggiorna' : submitting ? 'Invio…' : 'Invia disponibilità'}
            </button>
            {done && <div className={styles.sub}>Grazie! Puoi modificare e rinviare quando vuoi.</div>}
          </div>
        )}

        {closed && <div className={styles.card}>Le risposte per questa sessione sono chiuse.</div>}

        {/* Tally — who can make it */}
        {plan.slots.length > 0 && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Disponibilità del gruppo</h2>
            <div className={styles.tally}>
              {plan.slots.map((slot) => {
                const yes = plan.responses.filter((r) =>
                  r.slots.some((s) => s.slotId === slot.id && s.availability === 'yes'),
                );
                const maybe = plan.responses.filter((r) =>
                  r.slots.some((s) => s.slotId === slot.id && s.availability === 'maybe'),
                );
                return (
                  <div key={slot.id} className={styles.tallyRow}>
                    <div className={styles.slotWhen}>
                      {formatSlot(slot.start, slot.durationMin)} — ✓ {yes.length}
                      {maybe.length ? ` · ~ ${maybe.length}` : ''}
                    </div>
                    {(yes.length > 0 || maybe.length > 0) && (
                      <div className={styles.tallyNames}>
                        {yes.map((r) => r.characterName).join(', ')}
                        {maybe.length ? ` · forse: ${maybe.map((r) => r.characterName).join(', ')}` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className={styles.footer}>Pianificato con GM Binder</div>
      </div>
    </div>
  );
}
